import type {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,

  RawAxiosRequestHeaders,
} from 'axios'
import type {
  AxiosCacheInstance,
  InternalCacheRequestConfig,
  MemoryStorage,
} from 'axios-cache-interceptor'
import axios from 'axios'
import {
  buildMemoryStorage,
  setupCache,
} from 'axios-cache-interceptor'
import { storeToRefs } from 'pinia'
import qs from 'qs'

const TTL = 1000 * 60 * 30
function isValidHttpStatus(status: number): boolean {
  return status >= 200 && status < 400
}

declare module 'axios' {
  interface AxiosResponse<T = any> extends Promise<T> {}
}

function createApiInstance(baseURL: string, headers: RawAxiosRequestHeaders | undefined, overrides: AxiosRequestConfig = {}): AxiosInstance {
  return axios.create({
    baseURL,
    headers,
    paramsSerializer: {
      serialize: params => qs.stringify(params, { arrayFormat: 'brackets' }),
    },
    ...overrides,
  })
}

abstract class HttpClient {
  protected readonly api: AxiosCacheInstance
  private cache: MemoryStorage

  public constructor(baseURL: string, headers?: RawAxiosRequestHeaders, overrides?: AxiosRequestConfig) {
    this.cache = buildMemoryStorage()
    this.api = setupCache(createApiInstance(baseURL, headers, overrides), {
      storage: this.cache,
      ttl: TTL,
      methods: ['get'],
      cachePredicate: {
        statusCheck: status => isValidHttpStatus(status),
      },
      staleIfError: false,
    })
    this._initializeResponseInterceptor()
  }

  public invalidateCache = () => {
    this.cache.data = new Map()
  }

  private _initializeResponseInterceptor = () => {
    this.api.interceptors.response.use(
      this._handleResponse,
      this._handleError,
    )
    this.api.interceptors.request.use(
      this._handleRequest,
      this._handleError,
    )
  }

  private _handleRequest = async (config: InternalCacheRequestConfig): Promise<InternalCacheRequestConfig> => {
    if (config.method !== 'get')
      this.invalidateCache()

    return config
  }

  private _handleResponse = ({ data }: AxiosResponse) => data

  protected _handleError = (error: AxiosError) => {
    const errorsStore = useErrorsStore()
    const { error: oliveError } = storeToRefs(errorsStore)
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx

      const response = error.response
      const data: any = response.data
      const errorMessage = data.messages ? data.messages[0] : error.message

      oliveError.value = new OliveError(errorMessage, error.status || 0)
      throw new OliveError(errorMessage, error.status as number)
    }
    else if (error.code === 'ERR_CANCELED') {
      // eslint-disable-next-line no-console
      console.info('Request was canceled')
    }
    else if (error.request) {
      // The request was made but no response was received
      throw new OliveError(`Error request: ${error.request}`, 0)
    }
    else {
      // Something happened in setting up the request that triggered an Error
      throw new OliveError(`Error: ${error.message}`, 0)
    }
  }
}

const singleton = HttpClient
export default singleton

export { createApiInstance }
